export interface ParentIds {
    show_id: string;
    show_name: string;
    collection_id: string;
    collection_name: string;
    category_id: string;
    category_name: string;
}

export const EMPTY_PARENT_PAYLOAD: ParentIds = {
    show_id: '',
    show_name: '',
    collection_id: '',
    collection_name: '',
    category_id: '',
    category_name: '',
};

const LEGACY_PARENT_KEYS = [
    'linked_show_id',
    'linked_collection_id',
    'linked_category_id',
] as const;

export type ItemsParentMutateAsync = (variables: {
    parentId: string;
    type: string;
}) => Promise<unknown>;

const PARENT_ENTITY_TYPES = new Set(['SHOW', 'COLLECTION']);

const cacheKey = (type: string, parentId: string) =>
    `${type.trim().toUpperCase()}:${String(parentId).trim()}`;

const entityLabel = (entity?: { id?: string; name?: string; title?: string }) =>
    entity?.name || entity?.title || '';

/** Normalize API response into stable parent id/name fields. */
export function normalizeParentsResponse(response: unknown): ParentIds {
    const payload =
        (response as { data?: Record<string, { id?: string; name?: string; title?: string }> })
            ?.data ?? (response as Record<string, { id?: string; name?: string; title?: string }>) ?? {};

    return {
        show_id: payload.show?.id ?? '',
        show_name: entityLabel(payload.show),
        collection_id: payload.collection?.id ?? '',
        collection_name: entityLabel(payload.collection),
        category_id: payload.category?.id ?? '',
        category_name: entityLabel(payload.category),
    };
}

export function pickParentPayloadFields(ids: ParentIds): ParentIds {
    return {
        show_id: ids.show_id || '',
        show_name: ids.show_name || '',
        collection_id: ids.collection_id || '',
        collection_name: ids.collection_name || '',
        category_id: ids.category_id || '',
        category_name: ids.category_name || '',
    };
}

/** Map legacy linked_* keys to the new parent payload shape. */
export function migrateLegacyParentFields(
    payload: Record<string, unknown> = {}
): ParentIds {
    const hasNewKeys =
        payload.show_id !== undefined ||
        payload.collection_id !== undefined ||
        payload.category_id !== undefined;

    if (hasNewKeys) {
        return pickParentPayloadFields({
            show_id: String(payload.show_id ?? ''),
            show_name: String(payload.show_name ?? ''),
            collection_id: String(payload.collection_id ?? ''),
            collection_name: String(payload.collection_name ?? ''),
            category_id: String(payload.category_id ?? ''),
            category_name: String(payload.category_name ?? ''),
        });
    }

    return pickParentPayloadFields({
        show_id: String(payload.linked_show_id ?? ''),
        show_name: '',
        collection_id: String(payload.linked_collection_id ?? ''),
        collection_name: '',
        category_id: String(payload.linked_category_id ?? ''),
        category_name: '',
    });
}

export function stripLegacyParentKeys<T extends Record<string, unknown>>(obj: T): T {
    const next = { ...obj };
    LEGACY_PARENT_KEYS.forEach((key) => {
        delete next[key];
    });
    return next;
}

export function createParentCache(): Map<string, ParentIds> {
    return new Map();
}

export async function resolveParent(
    mutateAsync: ItemsParentMutateAsync,
    cache: Map<string, ParentIds>,
    parentId: string,
    entityType: string
): Promise<ParentIds> {
    const id = String(parentId).trim();
    const type = entityType.trim().toUpperCase();
    if (!id || !PARENT_ENTITY_TYPES.has(type)) {
        return { ...EMPTY_PARENT_PAYLOAD };
    }

    const key = cacheKey(type, id);
    const cached = cache.get(key);
    if (cached) return cached;

    const response = await mutateAsync({ parentId: id, type });
    const normalized = pickParentPayloadFields(normalizeParentsResponse(response));
    cache.set(key, normalized);
    return normalized;
}

export async function resolveParentsForRequests(
    mutateAsync: ItemsParentMutateAsync,
    cache: Map<string, ParentIds>,
    requests: Array<{ parentId: string; type: string }>
): Promise<void> {
    await Promise.all(
        requests.map(({ parentId, type }) =>
            resolveParent(mutateAsync, cache, parentId, type)
        )
    );
}

function getFromCache(
    cache: Map<string, ParentIds>,
    parentId: string,
    entityType: string
): ParentIds {
    return cache.get(cacheKey(entityType, parentId)) ?? { ...EMPTY_PARENT_PAYLOAD };
}

function getItemRedirectionType(item: {
    redirection_to?: string;
    redirectionTo?: string;
}): string {
    return String(item.redirection_to || item.redirectionTo || '').trim().toUpperCase();
}

function getItemRedirectionId(item: {
    redirection_id?: string;
    redirectionId?: string;
}): string {
    return String(item.redirection_id || item.redirectionId || '').trim();
}

/** Collect unique COLLECTION / SHOW IDs for parent resolution. */
export function collectCollectionCardParentRequests(
    widgetItems: Array<{
        redirection_to?: string;
        redirectionTo?: string;
        redirection_id?: string;
        redirectionId?: string;
    }>
): Array<{ parentId: string; type: string }> {
    const requests: Array<{ parentId: string; type: string }> = [];
    const seen = new Set<string>();

    widgetItems.forEach((item) => {
        const type = getItemRedirectionType(item);
        const id = getItemRedirectionId(item);
        if (!id || (type !== 'COLLECTION' && type !== 'SHOW')) return;

        const key = cacheKey(type, id);
        if (seen.has(key)) return;
        seen.add(key);
        requests.push({ parentId: id, type });
    });

    return requests;
}

/** Widget-level parent fields for CATEGORY source (category from form selection). */
export function buildCollectionCardSourceParentFields(
    categoryId?: string | number | null,
    categoryName?: string
): ParentIds {
    return pickParentPayloadFields({
        ...EMPTY_PARENT_PAYLOAD,
        category_id: String(categoryId ?? ''),
        category_name: categoryName || '',
    });
}

/** Widget-level parent fields for MANUAL source (no source entity parents). */
export function buildCollectionCardManualSourceParentFields(): ParentIds {
    return { ...EMPTY_PARENT_PAYLOAD };
}

export function enrichCollectionCardWidgetItems(
    widgetItems: any[],
    cache: Map<string, ParentIds>
): any[] {
    return widgetItems.map((item) => {
        const redirectionTo = getItemRedirectionType(item);
        const redirectionId = getItemRedirectionId(item);
        const { linked_show_id, linked_collection_id, linked_category_id, ...rest } = item;

        if (!redirectionId || (redirectionTo !== 'COLLECTION' && redirectionTo !== 'SHOW')) {
            return stripLegacyParentKeys({
                ...rest,
                redirection_to: redirectionTo,
                redirection_id: redirectionId,
                ...EMPTY_PARENT_PAYLOAD,
            });
        }

        const resolved = pickParentPayloadFields(
            getFromCache(cache, redirectionId, redirectionTo)
        );

        if (redirectionTo === 'COLLECTION') {
            return stripLegacyParentKeys({
                ...rest,
                redirection_to: redirectionTo,
                redirection_id: redirectionId,
                show_id: '',
                show_name: '',
                collection_id: resolved.collection_id || redirectionId,
                collection_name: resolved.collection_name,
                category_id: resolved.category_id,
                category_name: resolved.category_name,
            });
        }

        return stripLegacyParentKeys({
            ...rest,
            redirection_to: redirectionTo,
            redirection_id: redirectionId,
            show_id: resolved.show_id || redirectionId,
            show_name: resolved.show_name,
            collection_id: resolved.collection_id,
            collection_name: resolved.collection_name,
            category_id: resolved.category_id,
            category_name: resolved.category_name,
        });
    });
}

export function shouldResolveCollectionCardParents(sourceType: string): boolean {
    return sourceType === 'category' || sourceType === 'manual';
}
